import React, { useCallback, useRef, useState } from 'react';
import useClickOutside from '../utils/useClickOutside';

type SelectOptionProps = {
  onChange: (option: string) => void;
  option: {
    label: string;
    value: string;
  };
  selected: boolean;
  dataTestid: string;
};

const SelectOption: React.FC<SelectOptionProps> = (props) => {
  const { onChange, option, selected, dataTestid } = props;

  const handleClick = (): void => onChange(option.value);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <li
      // id="listbox-option-0"
      role="option"
      aria-selected={selected}
      className="flex text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 hover:bg-litlingo-gray-1"
      onClick={handleClick}
    >
      <span
        data-testid={`${option.value}-${dataTestid}`}
        className={`block text-left truncate ${selected ? 'font-semibold' : 'font-normal'}`}
      >
        {option.label}
      </span>

      {selected && (
        <span className="inset-y-0 right-0 flex items-center pr-4">
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      )}
    </li>
  );
};

type SelectProps = {
  onChange: (option: string) => void;
  id?: boolean | 'dialog' | 'menu' | 'false' | 'true' | 'listbox' | 'tree' | 'grid' | undefined;
  placeholder?: string;
  dataTestid?: string;
  options: {
    label: string;
    value: string;
  }[];
  className?: string;
  value: string;
};

const Select: React.FC<SelectProps> = (props) => {
  const { id, onChange, options, placeholder, value, dataTestid, className } = props;

  const [isOpen, setIsOpen] = useState(false);

  // click outside functionality
  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  const selectedOption = options.find((option) => option.value === value);
  const handleClick = (): void => setIsOpen(!isOpen);
  const handleChange = (option: string): void => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <span className="inline-block w-full rounded-md shadow-sm">
        <button
          data-testid={dataTestid || 'select'}
          type="button"
          aria-haspopup={id || 'listbox'}
          aria-expanded="true"
          className="cursor-default relative w-full rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2 text-left focus:outline-none focus:shadow-outline-blue focus:border-litlingo-success transition ease-in-out duration-150 sm:text-sm sm:leading-5"
          onClick={handleClick}
        >
          <span className="block truncate text">
            {selectedOption ? selectedOption.label : placeholder || 'Select an option'}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </span>

      {isOpen && (
        <div
          className={`z-50 absolute mt-1 w-full rounded-md bg-white shadow-lg overflow-auto ${
            className || ''
          }`}
        >
          <ul
            tabIndex={-1}
            role={(id as string) || 'listbox'}
            // aria-activedescendant="listbox-item-3"
            className="max-h-60 rounded-md py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5"
          >
            {options.map((option) => (
              <SelectOption
                key={`${option.label}-${option.value}`}
                onChange={handleChange}
                option={option}
                selected={option.value === value}
                dataTestid={`${dataTestid || 'select'}-option`}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Select;
